/**
 * Included Training - PDP Integration
 *
 * The HTML structure (fieldsets, card layout) is rendered server-side by
 * included-training-pdp.liquid. This file adds interactivity only:
 *
 *  1. Fetches the full variant list from /products/handle.js
 *  2. Marks sold-out location buttons as disabled
 *  3. On location change: marks unavailable dates disabled (CSS hides them)
 *     and auto-selects the first available date
 *  4. Updates the price / status display on variant selection
 *  5. "Add to order" button immediately adds the training to cart as a
 *     separate line item (no drawer opens). For kits that include N× of a
 *     training, the button stays active until N slots are filled; the counter
 *     shows "X/N trainings added". "Remove from order" removes all slots.
 *     The main kit add-to-cart is unintercepted — training is never bundled.
 *
 * Depends on: FoxTheme global (routes, utils, Currency, pubsub)
 */
(function () {

  /* ------------------------------------------------------------------ */
  /*  Location address helper (mirrors product-information-blocks.liquid) */
  /* ------------------------------------------------------------------ */

  function findAddressByLocation(selectedLocation, addresses) {
    var raw = selectedLocation.toLowerCase().trim();
    var parts = raw.split(/\s*[-–—]\s*/);
    var place = (parts.length > 1 ? parts[parts.length - 1] : raw).trim();
    for (var i = 0; i < addresses.length; i++) {
      if (typeof addresses[i] === 'string' && addresses[i].toLowerCase().indexOf(place) !== -1) {
        return addresses[i];
      }
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /*  Tickets remaining helper (mirrors variant-selects.js)             */
  /* ------------------------------------------------------------------ */

  function updateTicketsDisplay(card, variant, handle) {
    var el = card.querySelector('[data-tickets-left]');
    if (!el) return;
    var numberEl = el.querySelector('[data-ticket-number]');
    var countEl  = el.querySelector('[data-tickets-count]');
    if (!countEl) return;

    el.classList.remove('tickets-left--unavailable', 'tickets-left--low');

    /* Prefer inventory_quantity already set on the variant object (from the live
       section fetch, which covers all variants including those beyond the 250-variant
       Liquid limit). Fall back to the server-rendered itd-inventory map for the
       initial page-load state of the first 250 variants. */
    var qty = variant ? variant.inventory_quantity : undefined;
    var available = variant ? variant.available : false;

    if (variant && variant.id && handle && (qty === null || qty === undefined)) {
      var invEl = document.getElementById('itd-inventory-' + handle);
      if (invEl) {
        try {
          var invMap = JSON.parse(invEl.textContent || '{}');
          var invData = invMap[String(variant.id)];
          if (invData) {
            qty = invData.inventory_quantity;
            available = invData.available;
          }
        } catch (e) {}
      }
    }

    if (!available) {
      if (numberEl) numberEl.textContent = '✕';
      countEl.textContent = 'Sold out';
      el.classList.add('tickets-left--unavailable');
    } else if (qty > 0) {
      if (numberEl) numberEl.textContent = qty;
      countEl.textContent = 'ticket' + (qty !== 1 ? 's' : '') + ' remaining';
      if (qty <= 5) el.classList.add('tickets-left--low');
    } else {
      if (numberEl) numberEl.textContent = '✓';
      countEl.textContent = 'Available to book';
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function renderPrice(cents) {
    try {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function getVariantByOptions(variants, opts) {
    return variants.find(function (v) {
      return v.options.every(function (o, i) { return o === opts[i]; });
    });
  }

  function getCurrentSelections(card) {
    return Array.from(card.querySelectorAll('fieldset[data-option-index]')).map(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : '';
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Availability                                                       */
  /* ------------------------------------------------------------------ */

  function applyLocationAvailability(card, variants) {
    var fieldsets = card.querySelectorAll('fieldset[data-option-index]');
    if (!fieldsets.length) return;
    var locFs = fieldsets[0];

    locFs.querySelectorAll('input[type="radio"]').forEach(function (input) {
      var hasStock = variants.some(function (v) {
        return v.option1 === input.value && v.available;
      });
      input.classList.toggle('disabled', !hasStock);
    });
  }

  function applyDateAvailability(card, variants, selectedLocation) {
    var fieldsets = card.querySelectorAll('fieldset[data-option-index]');
    if (fieldsets.length < 2) return;
    var dateFs = fieldsets[1];

    dateFs.querySelectorAll('input[type="radio"]').forEach(function (input) {
      var variant = getVariantByOptions(variants, [selectedLocation, input.value]);
      var available = !!(variant && variant.available);
      input.classList.toggle('disabled', !available);
    });
  }

  function selectFirstAvailableDate(card) {
    var fieldsets = card.querySelectorAll('fieldset[data-option-index]');
    if (fieldsets.length < 2) return;
    var first = fieldsets[1].querySelector('input[type="radio"]:not(.disabled)');
    if (first) first.click();
  }

  /* ------------------------------------------------------------------ */
  /*  SessionStorage - persist training map for the cart drawer          */
  /* ------------------------------------------------------------------ */

  var STORAGE_KEY = 'included-training-map';

  function saveTrainingMap(mainHandle, trainingHandles) {
    try {
      var stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      stored[mainHandle] = trainingHandles;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ */
  /*  Slot state renderer                                                */
  /* ------------------------------------------------------------------ */

  function renderSlotState(card, handle, addedSlots, requiredQty, variants, toggleBtn, statusEl) {
    var slotsEl = card.querySelector('[data-itd-added-slots]');
    var slots = addedSlots[handle] || [];
    var count = slots.length;

    if (slotsEl) {
      if (count > 0) {
        var lines = slots.map(function (s) {
          var v = variants.find(function (v) { return String(v.id) === String(s.id); });
          return v && v.options && v.options.length
            ? v.options.join(' — ')
            : ('Variant #' + s.id);
        });
        slotsEl.innerHTML = lines.map(function (l) {
          return '<span class="itd-added-slot">✓ ' + l + '</span>';
        }).join('');
        slotsEl.hidden = false;
      } else {
        slotsEl.hidden = true;
        slotsEl.innerHTML = '';
      }
    }

    if (!toggleBtn) return;

    if (count >= requiredQty) {
      (toggleBtn.querySelector('.btn__text') || toggleBtn).textContent = 'Remove from order';
      toggleBtn.classList.remove('btn--secondary');
      toggleBtn.classList.add('btn--primary');
      toggleBtn.disabled = false;
      if (statusEl) statusEl.innerHTML =
        '<span class="itd-status--success">✓ All ' + count + ' training' + (count !== 1 ? 's' : '') + ' added to your order.</span>';
    } else if (count > 0) {
      (toggleBtn.querySelector('.btn__text') || toggleBtn).textContent = 'Add to order';
      toggleBtn.classList.remove('btn--primary');
      toggleBtn.classList.add('btn--secondary');
      toggleBtn.disabled = false;
      if (statusEl) statusEl.innerHTML =
        '<span class="itd-status--success">✓ ' + count + '/' + requiredQty + ' training' + (count !== 1 ? 's' : '') + ' added — select a date for your next training.</span>';
    } else {
      (toggleBtn.querySelector('.btn__text') || toggleBtn).textContent = 'Add to order';
      toggleBtn.classList.remove('btn--primary');
      toggleBtn.classList.add('btn--secondary');
      if (statusEl) statusEl.innerHTML =
        '<span class="text-subtext">Click ‘Add to order’ to include this training.</span>';
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Cart helpers                                                       */
  /* ------------------------------------------------------------------ */

  function addTrainingToCart(handle, variant, toggleBtn, statusEl, addedSlots, requiredQty, variants, card) {
    toggleBtn.disabled = true;
    (toggleBtn.querySelector('.btn__text') || toggleBtn).textContent = 'Adding…';

    fetch(FoxTheme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: 1 }] })
    })
    .then(function (res) {
      var ct = res.headers.get('content-type') || '';
      return ct.indexOf('application/json') !== -1 ? res.json() : null;
    })
    .then(function (addData) {
      if (addData && addData.status && addData.status >= 400) {
        throw new Error(addData.description || addData.message || 'Error adding training to cart');
      }
      addedSlots[handle].push({ id: variant.id, options: variant.options || [] });
      renderSlotState(card, handle, addedSlots, requiredQty, variants, toggleBtn, statusEl);
      // Refresh cart count + drawer contents without opening the drawer
      fetch(FoxTheme.routes.cart_url, Object.assign({}, FoxTheme.utils.fetchConfig()))
        .then(function (r) { return r.json(); })
        .then(function (cartJson) {
          var drawerEl = document.querySelector('cart-drawer');
          var drawSectionId = drawerEl && drawerEl.dataset && drawerEl.dataset.sectionId;
          if (drawSectionId) {
            return fetch(FoxTheme.routes.root_url + '?section_id=' + drawSectionId)
              .then(function (r) { return r.text(); })
              .then(function (html) {
                cartJson.sections = {};
                cartJson.sections[drawSectionId] = html;
                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
              });
          }
          cartJson.sections = {};
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        })
        .catch(function () {});
    })
    .catch(function (err) {
      console.error('[Included Training PDP] Add failed:', err);
      (toggleBtn.querySelector('.btn__text') || toggleBtn).textContent = 'Add to order';
      toggleBtn.disabled = false;
    });
  }

  function removeTrainingFromCart(handle, toggleBtn, statusEl, addedSlots, card) {
    function resetToEmpty() {
      addedSlots[handle] = [];
      var slotsEl = card.querySelector('[data-itd-added-slots]');
      if (slotsEl) { slotsEl.hidden = true; slotsEl.innerHTML = ''; }
      (toggleBtn.querySelector('.btn__text') || toggleBtn).textContent = 'Add to order';
      toggleBtn.classList.remove('btn--primary');
      toggleBtn.classList.add('btn--secondary');
      if (statusEl) statusEl.innerHTML =
        '<span class="text-subtext">Click ‘Add to order’ to include this training.</span>';
    }

    toggleBtn.disabled = true;

    fetch(FoxTheme.routes.cart_url, Object.assign({}, FoxTheme.utils.fetchConfig()))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart.items || []).filter(function (i) { return i.handle === handle; });
        if (!items.length) throw new Error('Item not found in cart');
        // Remove all line items for this training handle
        return Promise.all(items.map(function (item) {
          return fetch(FoxTheme.routes.cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
        }));
      })
      .then(function () {
        resetToEmpty();
        toggleBtn.disabled = false;
        return fetch(FoxTheme.routes.cart_url, Object.assign({}, FoxTheme.utils.fetchConfig()))
          .then(function (r) { return r.json(); });
      })
      .then(function (cartJson) {
        var drawerEl = document.querySelector('cart-drawer');
        var drawSectionId = drawerEl && drawerEl.dataset && drawerEl.dataset.sectionId;
        if (drawSectionId) {
          fetch(FoxTheme.routes.root_url + '?section_id=' + drawSectionId)
            .then(function (r) { return r.text(); })
            .then(function (html) {
              cartJson.sections = {};
              cartJson.sections[drawSectionId] = html;
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            })
            .catch(function () {
              cartJson.sections = {};
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            });
        } else {
          cartJson.sections = {};
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        }
      })
      .catch(function (err) {
        if (err.message === 'Item not found in cart') {
          resetToEmpty();
        } else {
          console.error('[Included Training PDP]', err);
        }
        toggleBtn.disabled = false;
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Initialise                                                         */
  /* ------------------------------------------------------------------ */

  function init() {
    var container = document.querySelector('.included-training-pdp');
    if (!container) return;

    var sectionId  = container.dataset.sectionId;
    var mainHandle = container.dataset.productHandle;

    var trainingHandles = Array.from(
      container.querySelectorAll('.itd-pdp__card[data-handle]')
    ).map(function (c) { return c.dataset.handle; });

    saveTrainingMap(mainHandle, trainingHandles);

    /* Map: handle → array of added slot objects {id, options[]} */
    var addedSlots = {};
    trainingHandles.forEach(function (h) { addedSlots[h] = []; });

    /* -- Check cart on init to restore button state ------------------- */

    fetch(FoxTheme.routes.cart_url, Object.assign({}, FoxTheme.utils.fetchConfig()))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        container.querySelectorAll('.itd-pdp__card[data-handle]').forEach(function (card) {
          var h = card.dataset.handle;
          var toggleBtn = card.querySelector('[data-itd-toggle]');
          if (!toggleBtn) return;
          var requiredQty = parseInt(card.dataset.itdRequiredQty || '1', 10);
          var statusEl = card.querySelector('[data-itd-status]');

          var cartItems = (cart.items || []).filter(function (i) { return i.handle === h; });
          var totalInCart = cartItems.reduce(function (sum, i) { return sum + i.quantity; }, 0);

          if (totalInCart > 0) {
            // Read embedded variants so we can show option labels immediately
            var jsonEl = document.getElementById('itd-variants-' + h);
            var initVariants = [];
            if (jsonEl) {
              try { initVariants = JSON.parse(jsonEl.textContent || '[]'); } catch (e) {}
            }
            // Populate addedSlots from cart line items
            var remaining = Math.min(totalInCart, requiredQty);
            cartItems.forEach(function (ci) {
              var v = initVariants.find(function (v) { return String(v.id) === String(ci.variant_id); });
              var opts = v ? (v.options || []) : [];
              for (var q = 0; q < ci.quantity && remaining > 0; q++, remaining--) {
                addedSlots[h].push({ id: ci.variant_id, options: opts });
              }
            });
            renderSlotState(card, h, addedSlots, requiredQty, initVariants, toggleBtn, statusEl);
          }
        });
      })
      .catch(function () {});

    /* -- Wire up each training card ----------------------------------- */

    container.querySelectorAll('.itd-pdp__card[data-handle]').forEach(function (card) {
      var handle = card.dataset.handle;
      var requiredQty = parseInt(card.dataset.itdRequiredQty || '1', 10);

      var variants = [];
      var jsonEl = document.getElementById('itd-variants-' + handle);
      if (jsonEl) {
        try { variants = JSON.parse(jsonEl.textContent || '[]'); } catch (e) {}
      }

      var priceEl  = card.querySelector('[data-itd-price]');
      var statusEl = card.querySelector('[data-itd-status]');

      function setStatus(variant) {
        /* Don't overwrite slot counter messages */
        if (addedSlots[handle] && addedSlots[handle].length > 0) return;
        if (variant && variant.available) {
          if (priceEl)  priceEl.innerHTML  = '<s>' + renderPrice(variant.price) + '</s>';
          if (statusEl) statusEl.innerHTML =
            '<span class="text-subtext">Click ‘Add to order’ to include this training.</span>';
        } else if (variant) {
          if (priceEl)  priceEl.innerHTML  = '';
          if (statusEl) statusEl.innerHTML =
            '<span class="text-subtext">This combination is unavailable. Please choose another.</span>';
        } else {
          if (priceEl)  priceEl.innerHTML  = '';
          if (statusEl) statusEl.innerHTML =
            '<span class="text-subtext">No training selected — you can add this later from your basket.</span>';
        }
      }

      function applyAvailability(variantData) {
        applyLocationAvailability(card, variantData);
        var locFs = card.querySelector('fieldset[data-option-index="0"]');
        if (locFs) {
          var checked = locFs.querySelector('input[type="radio"]:checked');
          if (checked) applyDateAvailability(card, variantData, checked.value);
        }
      }

      applyAvailability(variants);

      var locationOptionValueIds = {};
      var dateOptionValueIds    = {};
      var pickerSectionId = null;

      fetch('/products/' + handle, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var vs = doc.querySelector('variant-selects[data-section]');
          if (vs) pickerSectionId = vs.dataset.section;

          var srcLocFs = Array.from(doc.querySelectorAll('fieldset')).find(function (fs) {
            var legend = fs.querySelector('legend');
            return legend && legend.textContent.toLowerCase().includes('location');
          });

          if (srcLocFs) {
            var ourLocFs = card.querySelector('fieldset[data-option-index="0"]');
            srcLocFs.querySelectorAll('input[type="radio"]').forEach(function (src) {
              if (src.dataset.optionValueId) {
                locationOptionValueIds[src.value] = src.dataset.optionValueId;
              }
              if (ourLocFs) {
                var tgt = ourLocFs.querySelector('input[value="' + src.value.replace(/"/g, '\\"') + '"]');
                if (tgt) tgt.classList.toggle('disabled', src.classList.contains('disabled'));
              }
            });
          }

          var srcDateFsInit = Array.from(doc.querySelectorAll('fieldset')).find(function (fs) {
            var legend = fs.querySelector('legend');
            return legend && legend.textContent.toLowerCase().includes('date');
          });
          if (srcDateFsInit) {
            srcDateFsInit.querySelectorAll('input[type="radio"]').forEach(function (src) {
              if (src.dataset.optionValueId) {
                dateOptionValueIds[src.value] = src.dataset.optionValueId;
              }
            });
          }

          try {
            var startIdx = html.indexOf('var meta = ');
            if (startIdx !== -1) {
              var jsonStr = html.substring(startIdx + 'var meta = '.length);
              var braceCount = 0, endIdx = -1, inString = false, escapeNext = false;
              for (var i = 0; i < jsonStr.length; i++) {
                var ch = jsonStr[i];
                if (escapeNext) { escapeNext = false; continue; }
                if (ch === '\\') { escapeNext = true; continue; }
                if (ch === '"') { inString = !inString; continue; }
                if (!inString) {
                  if (ch === '{') braceCount++;
                  else if (ch === '}') { braceCount--; if (braceCount === 0) { endIdx = i + 1; break; } }
                }
              }
              if (endIdx !== -1) {
                var meta = JSON.parse(jsonStr.substring(0, endIdx));
                var metaVariants = (meta && meta.product && meta.product.variants) || [];
                if (metaVariants.length > variants.length) {
                  variants = metaVariants;
                  applyAvailability(variants);
                }
              }
            }
          } catch (e) {}
        })
        .catch(function () {});

      fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var jsVariants = (data && data.variants) || [];
          if (jsVariants.length > variants.length) {
            variants = jsVariants;
            applyAvailability(variants);
            // Back-fill options on any already-restored slots that had empty options
            addedSlots[handle].forEach(function (slot) {
              if (!slot.options || !slot.options.length) {
                var v = variants.find(function (v) { return String(v.id) === String(slot.id); });
                if (v && v.options) slot.options = v.options;
              }
            });
            // Re-render slot list now that we have option labels
            if (addedSlots[handle].length > 0) {
              var toggleBtn = card.querySelector('[data-itd-toggle]');
              renderSlotState(card, handle, addedSlots, requiredQty, variants, toggleBtn, statusEl);
            }
          }
        })
        .catch(function () {});

      /* -- Change listener ------------------------------------------- */

      card.addEventListener('change', function (e) {
        if (!e.target.matches('input[type="radio"]')) return;

        var fs  = e.target.closest('fieldset[data-option-index]');
        if (!fs) return;
        var idx = parseInt(fs.getAttribute('data-option-index'), 10);

        var legendVal = fs.querySelector('[data-selected-value]');
        if (legendVal) legendVal.textContent = e.target.value;

        if (idx === 0) {
          var selectedLocation = e.target.value;

          if (!selectedLocation) {
            var ourDateFs = card.querySelector('fieldset[data-option-index="1"]');
            if (ourDateFs) {
              ourDateFs.querySelectorAll('input[type="radio"]:checked').forEach(function (i) { i.checked = false; });
              var dateLegendVal = ourDateFs.querySelector('[data-selected-value]');
              if (dateLegendVal) dateLegendVal.textContent = '';
            }
            var toggleBtn = card.querySelector('[data-itd-toggle]');
            if (toggleBtn && addedSlots[handle].length < requiredQty) toggleBtn.disabled = true;
            setStatus(null);
            return;
          }

          /* Update location address display */
          var coordsEl = document.getElementById('itd-coordinates-' + handle);
          if (coordsEl) {
            try {
              var coords = JSON.parse(coordsEl.textContent || '[]');
              var locEl = card.querySelector('[data-itd-location-value]');
              if (locEl && coords.length) {
                var matched = findAddressByLocation(selectedLocation, coords);
                if (matched) locEl.textContent = matched;
              }
            } catch (e) {}
          }

          var optionValueId = locationOptionValueIds[selectedLocation];

          if (optionValueId && pickerSectionId) {
            var url = '/products/' + handle
              + '?option_values=' + optionValueId
              + '&section_id=' + pickerSectionId;

            fetch(url, { credentials: 'same-origin' })
              .then(function (r) { return r.text(); })
              .then(function (html) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var srcDateFs = Array.from(doc.querySelectorAll('fieldset')).find(function (fs) {
                  var legend = fs.querySelector('legend');
                  return legend && legend.textContent.toLowerCase().includes('date');
                });
                if (srcDateFs) {
                  srcDateFs.querySelectorAll('input[type="radio"]').forEach(function (srcInput) {
                    if (srcInput.dataset.optionValueId) {
                      dateOptionValueIds[srcInput.value] = srcInput.dataset.optionValueId;
                    }
                  });

                  var ourDateFs = card.querySelector('fieldset[data-option-index="1"]');
                  if (ourDateFs) {
                    ourDateFs.querySelectorAll('input[type="radio"]').forEach(function (ourInput) {
                      var srcInput = srcDateFs.querySelector('input[value="' + ourInput.value.replace(/"/g, '\\"') + '"]');
                      ourInput.classList.toggle('disabled', !srcInput || srcInput.classList.contains('disabled'));
                    });
                  }
                }
              })
              .catch(function () {
                applyDateAvailability(card, variants, selectedLocation);
              })
              .finally(function () {
                selectFirstAvailableDate(card);
              });
          } else {
            applyDateAvailability(card, variants, selectedLocation);
            selectFirstAvailableDate(card);
          }
        }

        var current     = getCurrentSelections(card);
        var allSelected = current.length >= 2 && current.every(function (v) { return !!v; });
        var variant     = allSelected ? (getVariantByOptions(variants, current) || null) : null;

        if (!variant && allSelected && pickerSectionId) {
          var _locId  = locationOptionValueIds[current[0]];
          var _dateId = dateOptionValueIds[current[1]];
          if (_locId && _dateId) {
            var _sel0 = current[0], _sel1 = current[1];
            fetch(
              '/products/' + handle
                + '?option_values=' + _locId + ',' + _dateId
                + '&section_id=' + pickerSectionId,
              { credentials: 'same-origin' }
            )
              .then(function (r) { return r.text(); })
              .then(function (sHtml) {
                var sDoc = new DOMParser().parseFromString(sHtml, 'text/html');
                var idInput = sDoc.querySelector('input[name="id"]');
                if (!idInput || !idInput.value) return;
                var nowSel = getCurrentSelections(card);
                if (nowSel[0] !== _sel0 || nowSel[1] !== _sel1) return;
                var resolvedId = parseInt(idInput.value, 10);
                var resolvedAvailable = true;
                var resolvedQty = null;

                /* Primary: read data-selected-variant — the section was fetched with
                   ?option_values=locId,dateId so this IS the resolved variant, and now
                   includes inventory_quantity (product-variant-picker.liquid explicit build). */
                var selVarScript = sDoc.querySelector('script[data-selected-variant]');
                if (selVarScript) {
                  try {
                    var selVarData = JSON.parse(selVarScript.textContent || '{}');
                    if (selVarData.id) {
                      resolvedAvailable = selVarData.available !== undefined ? selVarData.available : true;
                      resolvedQty = selVarData.inventory_quantity !== undefined ? selVarData.inventory_quantity : null;
                    }
                  } catch (e) {}
                }
                /* Fallback: data-variants-inventory (only covers first 250 variants) */
                if (resolvedQty === null) {
                  var invScript = sDoc.querySelector('script[data-variants-inventory]');
                  if (invScript) {
                    try {
                      var liveInv = JSON.parse(invScript.textContent || '{}');
                      var liveData = liveInv[String(resolvedId)];
                      if (liveData) {
                        resolvedAvailable = liveData.available !== undefined ? liveData.available : resolvedAvailable;
                        resolvedQty = liveData.inventory_quantity !== undefined ? liveData.inventory_quantity : null;
                      }
                    } catch (e) {}
                  }
                }

                var resolvedVariant = {
                  id: resolvedId,
                  available: resolvedAvailable,
                  inventory_quantity: resolvedQty,
                  price: variants.length ? variants[0].price : 0,
                  options: [_sel0, _sel1]
                };
                if (!getVariantByOptions(variants, [_sel0, _sel1])) {
                  variants.push(resolvedVariant);
                }
                var toggleBtn = card.querySelector('[data-itd-toggle]');
                if (toggleBtn && addedSlots[handle].length < requiredQty) toggleBtn.disabled = false;
                setStatus(resolvedVariant);
                updateTicketsDisplay(card, resolvedVariant, handle);
              })
              .catch(function () {});
          }
        }

        var toggleBtn = card.querySelector('[data-itd-toggle]');
        /* Only update enabled/disabled if slots are not yet full */
        if (toggleBtn && addedSlots[handle].length < requiredQty) {
          toggleBtn.disabled = !(variant && variant.available);
        }
        setStatus(variant);
        if (variant) updateTicketsDisplay(card, variant, handle);
      });

      /* -- Toggle button -------------------------------------------- */

      var toggleBtn = card.querySelector('[data-itd-toggle]');
      if (toggleBtn) {
        if (card.hasAttribute('data-itd-confirm')) {
          /* Confirm-required (in-person): add/remove on explicit click */
          toggleBtn.addEventListener('click', function () {
            if (addedSlots[handle].length >= requiredQty) {
              removeTrainingFromCart(handle, toggleBtn, statusEl, addedSlots, card);
            } else {
              var cur = getCurrentSelections(card);
              var allSel = cur.length >= 2 && cur.every(function (v) { return !!v; });
              var curVariant = allSel ? (getVariantByOptions(variants, cur) || null) : null;
              if (curVariant && curVariant.available) {
                addTrainingToCart(handle, curVariant, toggleBtn, statusEl, addedSlots, requiredQty, variants, card);
              }
            }
          });
        } else {
          /* Single-variant product (e.g. online course).
           * Read variants[0] at click time — not at setup time — because the
           * async .js fetch may not have returned yet when this listener is wired up. */
          toggleBtn.addEventListener('click', function () {
            if (addedSlots[handle].length >= requiredQty) {
              removeTrainingFromCart(handle, toggleBtn, statusEl, addedSlots, card);
            } else {
              var v = variants.length > 0 ? variants[0] : null;
              if (v) addTrainingToCart(handle, v, toggleBtn, statusEl, addedSlots, requiredQty, variants, card);
            }
          });
        }
      }
    });

    /* -- Sync button state when cart changes externally (e.g. drawer removal) --- */

    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      function (payload) {
        var cart = payload && payload.cart;
        if (!cart || !Array.isArray(cart.items)) return;

        container.querySelectorAll('.itd-pdp__card[data-handle]').forEach(function (card) {
          var h = card.dataset.handle;
          var btn = card.querySelector('[data-itd-toggle]');
          if (!btn) return;
          var stEl = card.querySelector('[data-itd-status]');
          var requiredQty = parseInt(card.dataset.itdRequiredQty || '1', 10);

          var cartItems = cart.items.filter(function (i) { return i.handle === h; });
          var cartCount = cartItems.reduce(function (sum, i) { return sum + i.quantity; }, 0);

          if (cartCount > 0) {
            /* Only sync if count has changed externally */
            if (cartCount !== addedSlots[h].length) {
              addedSlots[h] = [];
              var count = Math.min(cartCount, requiredQty);
              cartItems.forEach(function (ci) {
                for (var q = 0; q < ci.quantity && addedSlots[h].length < count; q++) {
                  addedSlots[h].push({ id: ci.variant_id, options: [] });
                }
              });
              renderSlotState(card, h, addedSlots, requiredQty, [], btn, stEl);
            }
          } else {
            /* Not in cart — reset to empty */
            if (addedSlots[h].length > 0) {
              addedSlots[h] = [];
              var slotsEl = card.querySelector('[data-itd-added-slots]');
              if (slotsEl) { slotsEl.hidden = true; slotsEl.innerHTML = ''; }
              (btn.querySelector('.btn__text') || btn).textContent = 'Add to order';
              btn.classList.remove('btn--primary');
              btn.classList.add('btn--secondary');
              if (stEl) stEl.innerHTML =
                '<span class="text-subtext">Click ‘Add to order’ to include this training.</span>';
            }
          }
        });
      }
    );

    /* -- Image gallery thumbnail switching ------------------------------- */

    initImageGallery();

    /* -- Intercept kit add-to-cart: skip drawer, scroll to training ----- */

    var form =
      document.querySelector('form#product-form-' + sectionId) ||
      document.querySelector('form[id*="product-form"][id*="' + sectionId + '"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]:not(cart-drawer form)');

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();

        var submitBtn = form.querySelector('button[type="submit"], button[name="add"]');
        if (submitBtn) submitBtn.classList.add('btn--loading');

        var formData = new FormData(form);
        var mainId   = formData.get('id');
        var mainQty  = formData.get('quantity') || '1';
        var properties = {};
        formData.forEach(function (value, key) {
          if (key.indexOf('properties[') === 0 && value) {
            properties[key.replace('properties[', '').replace(']', '')] = value;
          }
        });

        var sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        fetch(FoxTheme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [Object.assign(
              { id: Number(mainId), quantity: Number(mainQty) },
              Object.keys(properties).length ? { properties: properties } : {}
            )],
            sections: sectionsToBundle,
            sections_url: window.location.pathname
          })
        })
        .then(function (res) {
          var ct = res.headers.get('content-type') || '';
          return ct.indexOf('application/json') !== -1 ? res.json() : null;
        })
        .then(function (addData) {
          if (addData && addData.status && addData.status >= 400) {
            throw new Error(addData.description || 'Error adding to cart');
          }
          return fetch(FoxTheme.routes.cart_url, Object.assign({}, FoxTheme.utils.fetchConfig()))
            .then(function (r) { return r.json(); })
            .then(function (cartJson) {
              var drawerEl = document.querySelector('cart-drawer');
              var drawSectionId = drawerEl && drawerEl.dataset && drawerEl.dataset.sectionId;
              if (drawSectionId) {
                return fetch(FoxTheme.routes.root_url + '?section_id=' + drawSectionId)
                  .then(function (r) { return r.text(); })
                  .then(function (html) {
                    cartJson.sections = {};
                    cartJson.sections[drawSectionId] = html;
                    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
                  });
              }
              cartJson.sections = {};
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            });
        })
        .then(function () {
          /* Scroll to the training section instead of opening the cart drawer */
          var trainingSection = document.getElementById('IncludedTrainingPDP-' + sectionId);
          if (trainingSection) {
            trainingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            /* Offset for sticky header */
            setTimeout(function () { window.scrollBy(0, -80); }, 400);
          }
        })
        .catch(function (err) {
          console.error('[Included Training PDP]', err);
          var errorMsg = form.querySelector('.product-form__error-message');
          if (errorMsg) {
            errorMsg.textContent = err.message || 'Something went wrong. Please try again.';
            errorMsg.hidden = false;
            var errorWrapper = form.querySelector('.product-form__error-message-wrapper');
            if (errorWrapper) errorWrapper.hidden = false;
          }
        })
        .finally(function () {
          if (submitBtn) submitBtn.classList.remove('btn--loading');
        });

      }, true); /* capture phase — runs before FoxTheme's own submit listener */
    }
  }

  function initImageGallery() {
    document.querySelectorAll('[data-itd-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var handle = this.dataset.itdThumb;
        var mainImg = document.getElementById('itd-main-img-' + handle);
        if (mainImg) mainImg.src = this.dataset.src;
        document.querySelectorAll('[data-itd-thumb="' + handle + '"]').forEach(function (t) {
          t.classList.remove('is-active');
        });
        this.classList.add('is-active');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
